import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import Avatar from "@mui/material/Avatar/Avatar";
import { stringAvatar } from "../../utils/avatar";
import { HardSkill } from "./HardSkill";
import Accordion from "@mui/material/Accordion/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary/AccordionSummary";
import Typography from "@mui/material/Typography/Typography";
import AccordionDetails from "@mui/material/AccordionDetails/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React from "react";
import { SoftSkill } from "./SoftSkill";
import { Experiences } from "./Experiences";
import { Certification } from "./Certification";
import MuiButton from "@mui/material/Button/Button";
import {
  ICertificationsDto,
  IAddCurriculumDto,
  IExperiencesDto,
  IHardSkillsDto,
  ISoftSkillsDto,
  IGetCurriculumDto,
  IAddHardSkillsDto,
  ILinkedinSyncStatusDto,
  ILanguagesDto,
  IAddLanguagesDto,
} from "../../@types/curriculum.type";
import { storageUserGet } from "../../storage/storageUser";
import {
  addOrUpdateCurriculum,
  getCurriculumByResourceId,
  updateLinkedinSyncStatus,
} from "../../services/api/Curriculum/curriculumService";
import { getResourceById } from "../../services/api/resource/resourceService";
import { IUserProfile } from "../../@types/user.type";
import { toast } from "react-toastify";
import { ButtonEdited } from "../../components/ButtonEdited";
import TextField from "@mui/material/TextField/TextField";
import { ButtonCanceled } from "../../components/ButtonCancel";
import iconSave from "../../assets/_SaveIcon_.svg";
import iconLinkedin from "../../assets/icon-linkedin.svg";
import iconRefresh from "../../assets/icon-refresh.svg";
import CircularProgress from "@mui/material/CircularProgress";
import { Button } from "../../components/Button";
import { Language } from "./Language";
import { TUserInfo } from "../../@types/user-info.type";
import { SkillChangeWarning } from "./components/SkillChangeWarning";
import Tooltip from "@mui/material/Tooltip/Tooltip";

export const UserProfile: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const [userProfile, setUserProfile] = React.useState<IUserProfile>();
  const [user, setUser] = React.useState<TUserInfo>();
  const [linkedinSyncInProgress, setLinkedinSyncInProgress] = React.useState<boolean>(false);

  const [isUpdateLinkedin, setIsUpdateLinkedin] =
    React.useState<boolean>(false);
  const [linkedinUrl, setLinkedinUrl] = React.useState<string>();

  const [curriculum, setCurriculum] = useState<IGetCurriculumDto>();

  const [hardSkills, setHardSkills] = useState<IHardSkillsDto[]>([]);
  const [softSkills, setSoftSkills] = useState<ISoftSkillsDto[]>([]);
  const [experiences, setExperiences] = useState<IExperiencesDto[]>([]);
  const [certifications, setCertifications] = useState<ICertificationsDto[]>([]);
  const [languages, setLangagues] = useState<ILanguagesDto[]>([]);

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [changeInitialValue, setChangeInitialValue] = useState<boolean>(false);


  const loadInfo = async () => {
    if (user?.resourceId) {
      try {
        const profileResponse = (await getResourceById(user.resourceId))
          .result as IUserProfile;
        setUserProfile(profileResponse);
        setLinkedinUrl(profileResponse.urlLinkedinProfile);

        const response = (await getCurriculumByResourceId(
          user.resourceId
        )) as IGetCurriculumDto;
        if (response) {
          setCurriculum(response);
          setValues(response);

          if (response.urlLinkedinProfile) {
            setLinkedinUrl(response.urlLinkedinProfile);
          }

          if (response.isLinkedinSyncing) {
            setLinkedinSyncInProgress(true);
          }
        }

      } catch (error) {
        console.error("Error loading user profile:", error);
        toast.error("Erro ao carregar o perfil do usuário.");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!user?.resourceId) {
      setUser(storageUserGet());
      return;
    }

    loadInfo();
  }, [user]);

  function sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  async function checkLinkedinSync() {
    let isInProgress = true;
    while (isInProgress) {
      await sleep(30000);

      if (user?.resourceId) {
        const response = (await getCurriculumByResourceId(
          user.resourceId
        )) as IGetCurriculumDto;

        if (!response.isLinkedinSyncing) {
          isInProgress = false;
          setLinkedinSyncInProgress(false);
          await loadInfo();
          toast.info("Sincronização foi finalizada");
        }
      }
    }
  }

  useEffect(() => {
    if (!linkedinSyncInProgress)
      return

    checkLinkedinSync();
  }, [linkedinSyncInProgress])

  function setValues(obj: IGetCurriculumDto) {
    const hs = obj.hardSkills.map((x) => {
      return {
        id: x.referenceId,
        description: x.description,
        level: x.level,
      } as IHardSkillsDto;
    });

    const ss = obj.softSkills.map((x) => {
      return {
        id: x.referenceId,
        description: x.description,
      } as ISoftSkillsDto;
    });

    const e = obj.experiences.map((x) => {
      return {
        id: x.referenceId,
        description: x.description,
      } as IExperiencesDto;
    });

    const c = obj.certifications.map((x) => {
      return {
        id: x.certificationId,
        description: x.certification,
        certificationDate: x.certificationDate,
      } as ICertificationsDto;
    });

    const l = obj.languages.map((x) => {
      return {
        id: x.referenceId,
        description: x.description,
        levelId: x.levelId
      } as ILanguagesDto;
    });

    setHardSkills(hs);
    setSoftSkills(ss);
    setExperiences(e);
    setCertifications(c);
    setLangagues(l);
  }

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  async function syncLinkedin() {
    setLoading(true);
    if (user && user.resourceId) {
      const linkedin: ILinkedinSyncStatusDto = {
        resourceId: user.resourceId,
        sync: true,
        urlLinkedinProfile: linkedinUrl || "",
      };
      try {
        await updateLinkedinSyncStatus(linkedin);
        setLinkedinSyncInProgress(true);
        setLoading(false);
        toast.info("Iniciando sincronização com LinkedIn...");
      } catch (error) {
        setLoading(false);
        toast.error("Erro ao iniciar sincronização com LinkedIn.");
        console.error("Erro na sincronização:", error);
      }
    }
  }

  async function onSubmit() {
    setLoading(true);
    const curriculum = {
      resourceId: user?.resourceId,
      urlLinkedinProfile: linkedinUrl,
      hardSkills: hardSkills.map((x) => {
        return {
          hardSkillReferenceId: x.id,
        } as IAddHardSkillsDto;
      }),
      softSkills: softSkills.map((x) => x.id),
      experiences: experiences.map((x) => x.id),
      certifications: certifications,
      languages: languages.map((x) => {
        return {
          languageReferenceId: x.id,
          levelId: x.levelId
        } as IAddLanguagesDto;
      }),
    } as IAddCurriculumDto;

    try {
      await addOrUpdateCurriculum(curriculum);
      setChangeInitialValue(true);

      if (user && user.resourceId) {
        const response = (await getCurriculumByResourceId(
          user.resourceId
        )) as IGetCurriculumDto;

        if (response) {
          setCurriculum(response);
          setValues(response);
        }
      }

      setIsUpdateLinkedin(false);
      setIsEdit(false);
      toast.success("Perfil atualizado sucesso!");
    } catch (error) {
      toast.error("Error");
      console.log(error);
    } finally{
      setLoading(false);
    }
  }

  function updateLinkedin() {
    setIsUpdateLinkedin(true);
  }

  function cancelUpdateLinkedin() {
    setIsUpdateLinkedin(false);
  }

  function cancelEdit() {
    if (curriculum)
      setValues(curriculum);

    setIsEdit(false);
    setExpanded(false);
  }


  return (
    <>
      {loading ? (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CircularProgress size={100} />
        </div>
      ) : (
        <main className={styles.myProfile}>
          <div className={styles.mainInfo}>
            <Avatar className={styles.avatar} {...stringAvatar(user ? user.name : "")} />
            <div className={styles.info}>
              <div>
                <h2>{userProfile?.firstname + " " + userProfile?.lastname}</h2>
                <label>{userProfile?.email}</label>
                <label>{userProfile?.phone ? userProfile.phone : "-"}</label>
              </div>
              <div>
                <label>Responsavel: {userProfile?.managerName}</label>
                <label>Area: {userProfile?.areaName}</label>
                <label>Função: {userProfile?.positionName}</label>

              </div>
              {!linkedinSyncInProgress ? (
                <div style={{ display: "flex", justifyContent: "space-around", alignSelf: "flex-start", marginTop: "10px" }}>
                  {userProfile && !isUpdateLinkedin && (
                    <div>
                      <div>
                        <label>LinkedIn: </label>
                      </div>
                      <div className={styles.linkedin}>

                        {userProfile.urlLinkedinProfile && (
                          <a href={userProfile.urlLinkedinProfile} target="_blank" className={styles.linkedInUrl}>
                            <img
                              className={styles.iconLinkedin}
                              src={iconLinkedin}
                            ></img>
                          </a>
                        )}
                        {!userProfile?.urlLinkedinProfile && (
                          <img
                            className={styles.iconLinkedin}
                            src={iconLinkedin}
                          ></img>
                        )}

                        <ButtonEdited handleEditeItem={() => updateLinkedin()} />
                        <Tooltip disableTouchListener title="Sincronizar com LinkedIn">
                          <span style={{ height: "27px" }}>
                            <Button
                              onClick={() => { syncLinkedin() }}
                              style={{ cursor: linkedinSyncInProgress ? "not-allowed" : "pointer" }}
                              className={styles.iconRefresh}
                              content=""
                              iconUrl={iconRefresh}
                              disabled={linkedinSyncInProgress}
                            />
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  )}
                  {isUpdateLinkedin && (
                    <div className={styles.fields}>
                      <TextField
                        label="Linkedin"
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        value={linkedinUrl}
                        onChange={(event) => {
                          setLinkedinUrl(event.target.value);
                        }}
                        fullWidth
                      />

                      <MuiButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        className={styles.btnLinkedin}
                        onClick={onSubmit}
                      >
                        <img className={styles.icons} src={iconSave}></img>
                      </MuiButton>
                      <ButtonCanceled
                        handleCancelItem={() => cancelUpdateLinkedin()}
                      />
                    </div>
                  )}
                  <div></div>
                  <div></div>
                </div>
              ) : (
                <div style={{ 
                  display: "flex", 
                  flexDirection: "row", 
                  alignItems: "center", 
                  marginTop: "10px", 
                  maxWidth: "500px",
                  gap: "32px"
                }}>
                  <CircularProgress size={40} style={{ flexShrink: 0 }} />
                  <div style={{ fontSize: '12px', color: '#666', flex: 1 }}>
                    <span style={{ fontWeight: 'bold' }}>Sincronização iniciada!</span>
                    <br />
                    Sua solicitação foi enviada para a fila de processamento. Aguarde alguns minutos para a conclusão. Você pode continuar navegando no sistema enquanto finalizamos a operação.
                  </div>
                </div>
              )}
            </div>
          </div>
          <hr />
          <div className={styles.divAccordion}>
            <Accordion
              expanded={expanded === "panel1"}
              onChange={handleChange("panel1")}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1bh-content"
                id="panel1bh-header"
                className={styles.summaryAccordion}
              >
                <Typography
                  component="span"
                  sx={{ width: "33%", flexShrink: 0 }}
                >
                  Hard Skills
                </Typography>
                <div className={styles.infos}>
                  {isEdit && <SkillChangeWarning changed={changeInitialValue} setValue={setChangeInitialValue} changedSkills={hardSkills} />}
                  <div className={styles.badges}>
                    <div>{hardSkills.length}</div>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails className={styles.extensionAccordion}>
                {expanded === "panel1" && (
                  <HardSkill
                    setValue={setHardSkills}
                    hardSkills={hardSkills}
                    isEdit={isEdit}
                  />
                )}
              </AccordionDetails>
            </Accordion>
            <Accordion
              expanded={expanded === "panel2"}
              onChange={handleChange("panel2")}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2bh-content"
                id="panel2bh-header"
                className={styles.summaryAccordion}
              >
                <Typography
                  component="span"
                  sx={{ width: "33%", flexShrink: 0 }}
                >
                  Soft Skills
                </Typography>
                <div className={styles.infos}>
                  {isEdit && <SkillChangeWarning changed={changeInitialValue} setValue={setChangeInitialValue} changedSkills={softSkills} />}
                  <div className={styles.badges}>
                    <div> {softSkills.length}</div>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {expanded === "panel2" && (
                  <SoftSkill
                    setValue={setSoftSkills}
                    softSkills={softSkills}
                    isEdit={isEdit}
                  />
                )}
              </AccordionDetails>
            </Accordion>
            <Accordion
              expanded={expanded === "panel3"}
              onChange={handleChange("panel3")}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel3bh-content"
                id="panel3bh-header"
                className={styles.summaryAccordion}
              >
                <Typography
                  component="span"
                  sx={{ width: "33%", flexShrink: 0 }}
                >
                  Experiências
                </Typography>
                <div className={styles.infos}>
                  {isEdit && <SkillChangeWarning changed={changeInitialValue} setValue={setChangeInitialValue} changedSkills={experiences} />}
                  <div className={styles.badges}>
                    <div> {experiences.length}</div>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {expanded === "panel3" && (
                  <Experiences
                    setValue={setExperiences}
                    experiencesValue={experiences}
                    isEdit={isEdit}
                  />
                )}
              </AccordionDetails>
            </Accordion>
            <Accordion
              expanded={expanded === "panel4"}
              onChange={handleChange("panel4")}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel4bh-content"
                id="panel4bh-header"
                className={styles.summaryAccordion}
              >
                <Typography
                  component="span"
                  sx={{ width: "33%", flexShrink: 0 }}
                >
                  Certificações
                </Typography>
                <div className={styles.infos}>
                  {isEdit && <SkillChangeWarning changed={changeInitialValue} setValue={setChangeInitialValue} changedSkills={certifications} />}
                  <div className={styles.badges}>
                    <div> {certifications.length}</div>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {expanded === "panel4" && (
                  <Certification
                    setValue={setCertifications}
                    certificationValue={certifications}
                    isEdit={isEdit}
                  />
                )}
              </AccordionDetails>
            </Accordion>
            <Accordion
              expanded={expanded === "panel5"}
              onChange={handleChange("panel5")}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel5bh-content"
                id="panel5bh-header"
                className={styles.summaryAccordion}
              >
                <Typography
                  component="span"
                  sx={{ width: "33%", flexShrink: 0 }}
                >
                  Idiomas
                </Typography>
                <div className={styles.infos}>
                  {isEdit && <SkillChangeWarning changed={changeInitialValue} setValue={setChangeInitialValue} changedSkills={languages} />}
                  <div className={styles.badges}>
                    <div> {languages.length}</div>
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {expanded === "panel5" && (
                  <Language
                    setValue={setLangagues}
                    languageValue={languages}
                    isEdit={isEdit}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          </div>
          <div className={styles.divBtn}>
            {isEdit ? (
              <>
                <MuiButton onClick={cancelEdit} variant="outlined" color="primary">
                  Cancelar edição
                </MuiButton>

                <MuiButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  onClick={onSubmit}
                  // disabled={!isEnabled || loading}
                  className={styles.btnsSave}
                >
                  Salvar
                </MuiButton>
              </>
            ) : (
              <MuiButton
                onClick={() => setIsEdit(true)}
                variant="contained"
                color="primary"
              >
                Editar
              </MuiButton>
            )}
          </div>
        </main>
      )}
    </>
  );
};
